import { useMemo } from 'react';

import { OriginalWattwatchersMeter, SwitchData, WattwatchersMeter } from 'clipsal-cortex-types/src/api/api-ww-meter';
import { PaginatedResponse } from 'clipsal-cortex-types/src/common/types';
import {
  CATCH_POWER_MANUFACTURER_ID,
  WATTWATCHERS_MANUFACTURER_ID,
} from 'clipsal-cortex-utils/src/constants/common-constants';
import { getStringifiedQueryParams } from 'clipsal-cortex-utils/src/formatting/query-params';

import { Assignment } from '../../../../api/api-assignments';
import { get } from '../../../../api/api-helpers';
import { SiteData } from '../../../../api/api-site';
import { baseApi } from '../../../../app/baseApi';
import {
  ChannelCategoryData,
  LogData,
  LongEnergyData,
  LongIntervalMetrologyData,
  MeterSearchData,
  ShortEnergyData,
  ShortIntervalMetrologyData,
  SiteMeterData,
} from './meter-config-types';

type CommonParams = { timezone: string };
type LongEnergyQueryParams = { reading_date: string } & CommonParams;
export type UpdateMeterDetailsBody = {
  id: string;
  channels: Omit<OriginalWattwatchersMeter['channels'][number], 'categoryLabel' | 'pending'>[];
  switches?: Omit<SwitchData, 'categoryLabel' | 'pending'>[];
};
const POLLING_INTERVAL_TIMER_MS = 5000;

export const meterConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchSites: builder.query<PaginatedResponse<SiteData>, string>({
      query: (search) => `/fleet/sites?search_term=${search}&imit=5&offset=0`,
      providesTags: (_, __, search) => [{ type: 'Sites', search }],
    }),
    searchMeters: builder.query<MeterSearchData[], string>({
      query: (searchTerm) => `/v2/meters/search?search_term=${searchTerm}`,
      providesTags: (_, __, searchTerm) => [{ type: 'MeterSearch', searchTerm }],
    }),
    getFirstEnergyTimestamp: builder.query<{ timestamp: number }, { deviceId: string; params: CommonParams }>({
      query: ({ deviceId, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        return `/v1/wattwatchers/devices/${deviceId}/first?${stringifiedParams}`;
      },
      providesTags: (_, __, params) => [{ type: 'FirstEnergyTimestamp', ...params }],
    }),
    getMeterDetails: builder.query<OriginalWattwatchersMeter, string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}`,
      providesTags: (_, __, deviceId) => [{ type: 'OriginalWattWatchersMeter', deviceId }],
    }),
    getSiteMeterDetails: builder.query<WattwatchersMeter, number | null>({
      query: (siteDeviceId) => `/fleet/wattwatchers/installed_meters/${siteDeviceId}`,
      providesTags: (_, __, siteDeviceId) => [{ type: 'WattWatchersMeter', siteDeviceId }],
    }),
    getShortEnergy: builder.query<ShortEnergyData[], string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}/short_energy`,
      providesTags: (_, __, deviceId) => [{ type: 'ShortEnergy', deviceId }],
    }),
    getShortIntervalMetrology: builder.query<ShortIntervalMetrologyData[], number>({
      query: (deviceId) => `/v1/site_devices/${deviceId}/short_interval_metrology`,
      providesTags: (_, __, deviceId) => [{ type: 'ShortIntervalMetrology', deviceId }],
    }),
    getCombinedShortEnergyData: builder.query<ShortEnergyData[][], string[]>({
      queryFn: async (deviceIds) => {
        const data = await Promise.all(
          deviceIds.map((deviceId) =>
            get<ShortEnergyData[]>(`/fleet/wattwatchers/installed_meters/${deviceId}/short_energy`)
          )
        );

        return {
          data,
        };
      },
      providesTags: (result, error, arg) => [
        ...arg.map((deviceId) => ({
          id: deviceId,
          type: 'ShortEnergy' as const,
        })),
      ],
    }),
    getLongEnergy: builder.query<LongEnergyData[], { deviceId: string; params: LongEnergyQueryParams }>({
      query: ({ deviceId, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        return `/v1/wattwatchers/devices/${deviceId}/long_energy?${stringifiedParams}`;
      },
      providesTags: (_, __, params) => [{ type: 'LongEnergy', ...params }],
    }),
    getLongIntervalMetrology: builder.query<
      LongIntervalMetrologyData[],
      { siteDeviceId: number; params: LongEnergyQueryParams }
    >({
      query: ({ siteDeviceId, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        return `/v1/site_devices/${siteDeviceId}/long_interval_metrology?${stringifiedParams}`;
      },
      providesTags: (_, __, args) => [{ type: 'LongIntervalMetrology', ...args }],
    }),
    getCombinedLongEnergyData: builder.query<
      LongEnergyData[][],
      { deviceIds: string[]; params: LongEnergyQueryParams }
    >({
      queryFn: async ({ deviceIds, params }) => {
        const stringifiedParams = getStringifiedQueryParams(params);
        const data = await Promise.all(
          deviceIds.map((deviceId) =>
            get<LongEnergyData[]>(`/fleet/wattwatchers/installed_meters/${deviceId}/long_energy?${stringifiedParams}`)
          )
        );

        return {
          data,
        };
      },
      providesTags: (result, error, arg) => [
        ...arg.deviceIds.map((deviceId) => ({
          id: deviceId,
          type: 'LongEnergy' as const,
          ...arg.params,
        })),
      ],
    }),
    getChannelCategories: builder.query<ChannelCategoryData[], void>({
      query: () => `/v1/wattwatchers/channel_categories`,
      providesTags: ['ChannelCategories'],
    }),
    getAssignments: builder.query<Assignment[], void>({
      query: () => '/fleet/assignments',
      providesTags: () => ['Assignments'],
    }),
    getLogs: builder.query<PaginatedResponse<LogData>, string>({
      query: (deviceId) => `/v1/wattwatchers/devices/${deviceId}/logs?ordering=-timestamp`,
      providesTags: (_, __, deviceId) => [{ type: 'Logs', deviceId }],
    }),
    updateMeterDetails: builder.mutation<OriginalWattwatchersMeter, UpdateMeterDetailsBody>({
      query: (body) => {
        return {
          url: `/v1/wattwatchers/devices/${body.id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { id: deviceId }) => [
        { type: 'OriginalWattWatchersMeter', deviceId },
        { type: 'Logs', deviceId },
      ],
    }),
    updateSiteMeterDetails: builder.mutation<WattwatchersMeter, WattwatchersMeter>({
      query: (body) => {
        return {
          url: `/fleet/wattwatchers/installed_meters/${body.installed_device_id}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: (_, __, { installed_device_id: siteDeviceId, ww_device_id: deviceId }) => [
        { type: 'WattWatchersMeter', siteDeviceId },
        { type: 'Logs', deviceId },
      ],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useSearchSitesQuery,
  useSearchMetersQuery,
  useGetFirstEnergyTimestampQuery,
  useGetMeterDetailsQuery,
  useGetSiteMeterDetailsQuery,
  useGetLongEnergyQuery,
  useGetCombinedLongEnergyDataQuery,
  useGetShortEnergyQuery,
  useGetCombinedShortEnergyDataQuery,
  useGetShortIntervalMetrologyQuery,
  useGetLongIntervalMetrologyQuery,
  useGetChannelCategoriesQuery,
  useGetLogsQuery,
  useGetAssignmentsQuery,
  useUpdateMeterDetailsMutation,
  useUpdateSiteMeterDetailsMutation,
} = meterConfigApi;

const SUPPORTED_METER_MANUFACTURER_IDS = [WATTWATCHERS_MANUFACTURER_ID, CATCH_POWER_MANUFACTURER_ID];

export const useSearchSites = (search: string, skip?: boolean) => {
  const queryResult = useSearchSitesQuery(search, { skip: skip || !search });
  const { data } = queryResult;

  const searchResults = useMemo(() => {
    const siteMeters: SiteMeterData[] = (data?.data ?? []).flatMap((site) => {
      const supportedMeters = site?.meters
        ?.filter((meter) => SUPPORTED_METER_MANUFACTURER_IDS.includes(meter.meta_data.manufacturer_id))
        .map((meter) => ({
          siteId: site.clipsal_solar_id,
          siteName: site.site_name,
          siteIdentifier: meter.site_identifier ?? '',
          siteDeviceId: meter.row_id ?? 0,
          serialNumber: meter.serial_number ?? '',
          manufacturerId: meter.meta_data.manufacturer_id ?? 0,
          manufacturerName: meter.meta_data.manufacturer.manufacturer_name ?? '',
          model: meter.meta_data.model,
        }));
      return supportedMeters ?? [];
    });
    return siteMeters;
  }, [data]);

  return { ...queryResult, searchResults };
};

export const useSearchMeters = (search: string, skip?: boolean) => {
  const queryResult = useSearchMetersQuery(search, { skip: skip || !search });

  const searchResults: SiteMeterData[] = useMemo(() => {
    return (queryResult?.data ?? [])
      .filter((meter) => SUPPORTED_METER_MANUFACTURER_IDS.includes(meter.manufacturer_id))
      .map((meter) => ({
        siteId: meter.clipsal_solar_id,
        siteName: meter.site_name,
        siteIdentifier: meter.site_identifier ?? '',
        siteDeviceId: meter.id ?? 0,
        serialNumber: meter.serial_number ?? '',
        manufacturerId: meter.manufacturer_id ?? 0,
        manufacturerName: meter.manufacturer_name ?? '',
        model: meter.model,
      }));
  }, [queryResult?.data]);

  return { ...queryResult, data: searchResults };
};

export const useFirstEnergyTimestamp = (deviceId: string, params: CommonParams) => {
  const queryResult = useGetFirstEnergyTimestampQuery({ deviceId, params });
  return { ...queryResult, data: queryResult.data || { timestamp: 0 } };
};

export const useMeter = (deviceId: string, skip?: boolean) => {
  const queryResult = useGetMeterDetailsQuery(deviceId, {
    skip: skip || !deviceId,
    pollingInterval: deviceId ? POLLING_INTERVAL_TIMER_MS : undefined,
  });
  return queryResult;
};

export const useSiteMeter = (siteDeviceId: number | null) => {
  const queryResult = useGetSiteMeterDetailsQuery(siteDeviceId, { skip: !siteDeviceId });
  return queryResult;
};

export const useShortEnergy = (deviceId: string, skip?: boolean) => {
  const skipPolling = skip || !deviceId;
  const queryResult = useGetShortEnergyQuery(deviceId, {
    skip: skipPolling,
    pollingInterval: skipPolling ? undefined : POLLING_INTERVAL_TIMER_MS,
  });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useShortIntervalMetrology = (deviceId: number, skip?: boolean) => {
  const skipPolling = skip || !deviceId;
  const queryResult = useGetShortIntervalMetrologyQuery(deviceId, {
    skip: skipPolling,
    pollingInterval: skipPolling ? undefined : POLLING_INTERVAL_TIMER_MS,
  });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useCombinedShortEnergyData = (deviceIds: string[], skip?: boolean) => {
  const skipPolling = skip || !deviceIds.length;
  const queryResult = useGetCombinedShortEnergyDataQuery(deviceIds, {
    skip: skipPolling,
    pollingInterval: skipPolling ? undefined : POLLING_INTERVAL_TIMER_MS,
  });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useLongEnergy = (deviceId: string, params: LongEnergyQueryParams, skip?: boolean) => {
  const queryResult = useGetLongEnergyQuery({ deviceId, params }, { skip: skip || !deviceId });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useLongIntervalMetrology = (siteDeviceId: number, params: LongEnergyQueryParams, skip?: boolean) => {
  const skipPolling = skip || !siteDeviceId;
  const queryResult = useGetLongIntervalMetrologyQuery(
    { siteDeviceId, params },
    {
      skip: skipPolling,
    }
  );
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useCombinedLongEnergyData = (deviceIds: string[], params: LongEnergyQueryParams, skip?: boolean) => {
  const queryResult = useGetCombinedLongEnergyDataQuery({ deviceIds, params }, { skip: skip || !deviceIds.length });
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useChannelCategories = () => {
  const queryResult = useGetChannelCategoriesQuery();
  return { ...queryResult, data: queryResult.data ?? [] };
};

export const useLogs = (deviceId: string) => {
  const queryResult = useGetLogsQuery(deviceId);
  return { ...queryResult, data: queryResult.data?.data ?? [] };
};

export const useAssignments = () => {
  const queryResult = useGetAssignmentsQuery();
  return { ...queryResult, assignments: queryResult.data ?? [] };
};
